import { useState } from "react";
import { toast } from "sonner";
import { Hourglass, Sparkles, Loader2, Copy, Send, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageHeader, PageState, Empty, UpgradeGate } from "@/components/app/Common";
import { TaskForm, PARTIES } from "@/pages/ChiefOfStaff";
import { useLoad } from "@/hooks/useLoad";
import { api, errMsg } from "@/lib/api";
import { fmtDate } from "@/lib/ui";
import { useAuth } from "@/context/AuthContext";

const URG = { high: "bg-red-500/12 text-red-600 dark:text-red-400", medium: "bg-amber-500/12 text-amber-600 dark:text-amber-400", low: "bg-slate-500/12 text-slate-500" };
const party = (t) => PARTIES.find((p) => p[0] === t)?.[1] || "Someone";

const Rec = ({ r }) => (
  <div className="mt-2 rounded-lg border border-primary/25 bg-primary/[0.04] p-3 text-sm" data-testid={`waiting-rec-${r.task_id}`}>
    <div className="flex flex-wrap items-center gap-2"><Sparkles className="h-3.5 w-3.5 text-primary" /><span className={`text-[11px] rounded-full px-2 py-0.5 ${URG[r.urgency]}`}>{r.urgency} urgency</span><span>{r.action}</span></div>
    {r.message && (
      <div className="mt-2 flex gap-2 items-start">
        <p className="flex-1 text-xs text-muted-foreground whitespace-pre-line border-l-2 border-border pl-2">{r.message}</p>
        <Button size="sm" variant="ghost" onClick={() => navigator.clipboard.writeText(r.message).then(() => toast.success("Message copied"))} data-testid={`waiting-copy-${r.task_id}`}><Copy className="h-3.5 w-3.5 mr-1" />Copy</Button>
      </div>)}
  </div>
);

const Row = ({ t, rec, reload, canEdit }) => {
  const [busy, setBusy] = useState("");
  const run = async (kind, fn) => {
    setBusy(kind);
    try {
      await fn();
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
    setBusy("");
  };
  const chase = () => run("chase", async () => { await api.post(`/waiting/${t.id}/chase`, { note: "" }); toast.success("Follow-up logged"); });
  const resume = () => run("resume", async () => { await api.put(`/tasks/${t.id}`, { ...t, status: "open" }); toast.success("Back in your open tasks"); });
  return (
    <div className="py-4" data-testid={`waiting-row-${t.id}`}>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <div className="flex-1 min-w-[220px]">
          <div className="font-medium text-sm flex flex-wrap items-center gap-2">{t.title}
            {t.stalled && <span className="text-[11px] rounded-full px-2 py-0.5 bg-red-500/12 text-red-600 dark:text-red-400" data-testid={`waiting-stalled-${t.id}`}>Stalled</span>}</div>
          <div className="text-xs text-muted-foreground mt-0.5">Waiting on <b className="text-foreground">{t.waiting_on_name || party(t.waiting_on_type)}</b> ({party(t.waiting_on_type).toLowerCase()})
            {t.entity_name && ` · ${t.entity_name}`}{t.owner && ` · owner ${t.owner}`}{t.due_date && ` · due ${fmtDate(t.due_date)}`}</div>
        </div>
        <div className="text-xs text-right font-mono" data-testid={`waiting-days-${t.id}`}><div>{t.days_waiting ?? 0}d waiting</div><div className={t.stalled ? "text-red-500" : "text-muted-foreground"}>{t.days_quiet ?? 0}d quiet · chased {t.chases}x</div></div>
        {canEdit && <div className="flex gap-1.5">
          <Button size="sm" variant="outline" onClick={chase} disabled={!!busy} data-testid={`waiting-chase-${t.id}`}>{busy === "chase" ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Send className="h-3.5 w-3.5 mr-1" />}Log follow-up</Button>
          <Button size="sm" variant="ghost" onClick={resume} disabled={!!busy} data-testid={`waiting-resume-${t.id}`}><CheckCircle2 className="h-3.5 w-3.5 mr-1" />Got it</Button>
        </div>}
      </div>
      {rec && <Rec r={rec} />}
    </div>
  );
};

export default function WaitingOn() {
  const { user } = useAuth();
  const canEdit = user?.workspace?.role !== "viewer";
  const state = useLoad(() => Promise.all([api.get("/waiting"), api.get("/entities")]).then(([a, b]) => ({ ...a.data, ents: b.data })));
  const [busy, setBusy] = useState(false);
  if (state.gated) return <div><PageHeader eyebrow="AI Chief of Staff" title="Waiting On" /><UpgradeGate gated={state.gated} /></div>;
  const d = state.data;
  const recs = Object.fromEntries((d?.review?.items || []).map((r) => [r.task_id, r]));
  const stalled = (d?.items || []).filter((t) => t.stalled).length;
  const review = async () => {
    setBusy(true);
    try {
      await api.post("/waiting/review");
      state.reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
    setBusy(false);
  };
  return (
    <div>
      <PageHeader eyebrow="AI Chief of Staff" title="Waiting On" subtitle="Everything blocked on customers, vendors, accountants, lawyers, employees and other third parties — and what to do about the stalled ones.">
        <Button onClick={review} disabled={busy || !d?.items?.length} data-testid="waiting-review-btn">{busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1.5" />}Ask Claude what to chase</Button>
      </PageHeader>
      <PageState state={state}>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-6">
          {[["Waiting on others", d?.items?.length || 0, "waiting-count"], [`Stalled ${d?.stall_days || 5}+ days`, stalled, "waiting-stalled-count"]].map(([l, v, id]) => (
            <div key={id} className="surface p-4"><div className="text-xs text-muted-foreground">{l}</div><div className="text-2xl font-semibold font-mono mt-1" data-testid={id}>{v}</div></div>))}
        </div>
        {d?.review?.summary && <div className="surface p-4 mb-6 border-l-4 border-primary/50 text-sm flex gap-2" data-testid="waiting-review-summary"><Sparkles className="h-4 w-4 text-primary mt-0.5 shrink-0" /><span>{d.review.summary} <span className="text-xs text-muted-foreground">· {fmtDate(d.review.created_at)}</span></span></div>}
        <div className="surface p-6">
          {canEdit && <><div className="font-semibold text-sm mb-3">Track something you're waiting on</div><TaskForm ents={d?.ents || []} onSaved={state.reload} waiting /></>}
          {d?.items?.length ? <div className="divide-y divide-border mt-4">{d.items.map((t) => <Row key={t.id} t={t} rec={recs[t.id]} reload={state.reload} canEdit={canEdit} />)}</div>
            : <div className="mt-6"><Empty icon={Hourglass} title="Nothing is waiting on others" text="When a task depends on someone outside your control, track it here so it never goes quiet." testId="waiting-empty" /></div>}
        </div>
      </PageState>
    </div>
  );
}
